import { useState, useEffect } from "react";
import { useAws } from "@/context/AwsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Scale, TrendingUp, Database, Server as ServerIcon, Globe2, ShieldCheck,
  KeyRound, Lock, ClipboardCheck, CircleDot, Network, Boxes, Layers, HeartPulse,
  Eye, EyeOff, Cpu, MemoryStick, Plus, Play, Square as SquareIcon, RotateCw, Trash2,
  Table2, Loader2, Terminal,
} from "lucide-react";

// ---------- shared primitives (match AwsConsole dark AWS theme) ----------
const ORANGE = "#ff9900";

function Th({ children }) { return <th className="text-left font-semibold px-3 py-2 text-slate-300 whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${className}`}>{children}</td>; }
function Empty({ label }) { return <div className="text-center py-14 text-slate-400 text-sm border border-dashed border-[#414750] rounded-lg">{label}</div>; }

function Table({ cols, children, testid }) {
  return <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]" data-testid={testid}>
    <table className="w-full text-xs"><thead className="bg-[#1f2937] border-b border-[#414750]"><tr>{cols.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
      <tbody>{children}</tbody></table></div>;
}

function Header({ icon: Icon, title, sub, right }) {
  return <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-[#ff9900]" />
      <div><h1 className="text-xl font-semibold text-white">{title}</h1>
        {sub && <p className="text-slate-400 text-xs mt-0.5">{sub}</p>}</div>
    </div>{right}</div>;
}

function Section({ title, children, right }) {
  return <div><div className="flex items-center justify-between mb-2"><div className="text-sm font-semibold text-white">{title}</div>{right}</div>{children}</div>;
}

function Link({ children, onClick, testid }) {
  return <button onClick={onClick} data-testid={testid} className="text-[#4da3ff] hover:underline font-medium text-left">{children}</button>;
}

const HEALTH_COLOR = { healthy: "text-emerald-400", available: "text-emerald-400", active: "text-emerald-400",
  Deployed: "text-emerald-400", Enabled: "text-emerald-400", unhealthy: "text-rose-400", COMPLIANT: "text-emerald-400",
  NON_COMPLIANT: "text-rose-400", OK: "text-emerald-400", ALARM: "text-rose-400" };
function Status({ v }) {
  return <span className={`inline-flex items-center gap-1.5 ${HEALTH_COLOR[v] || "text-slate-300"}`}><CircleDot className="h-3 w-3" />{v}</span>;
}

// key/value grid used inside every detail sheet
function KV({ rows }) {
  return <div className="rounded-lg border border-[#414750] bg-[#0f1b2d] divide-y divide-[#2a323d]">
    {rows.filter(Boolean).map(([k, v], i) => (
      <div key={i} className="grid grid-cols-3 gap-3 px-3 py-2 text-xs">
        <div className="text-slate-400">{k}</div>
        <div className="col-span-2 text-slate-100 break-all">{v ?? "—"}</div>
      </div>
    ))}
  </div>;
}

// generic right-side detail sheet with tabs
function DetailSheet({ open, onClose, icon: Icon, title, subtitle, tabs, testid }) {
  const keys = tabs.map((t) => t.label);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-[#12203a] border-l border-[#414750] text-slate-100 w-full sm:max-w-2xl overflow-y-auto" data-testid={testid}>
        <SheetHeader className="mb-4">
          <SheetTitle className="text-white flex items-center gap-2 text-lg">
            {Icon && <Icon className="h-5 w-5 text-[#ff9900]" />}{title}
          </SheetTitle>
          {subtitle && <div className="text-xs text-slate-400 font-mono">{subtitle}</div>}
          <SheetDescription className="sr-only">{title} resource details</SheetDescription>
        </SheetHeader>
        <Tabs defaultValue={keys[0]}>
          <TabsList className="bg-[#0f1b2d] border border-[#414750] flex-wrap h-auto">
            {keys.map((k) => <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#ff9900]/15 data-[state=active]:text-[#ff9900] text-slate-300 text-xs" data-testid={`tab-${k}`}>{k}</TabsTrigger>)}
          </TabsList>
          {tabs.map((t) => <TabsContent key={t.label} value={t.label} className="mt-4 space-y-4">{t.content}</TabsContent>)}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function name(state, id) {
  const r = (state?.resources || []).find((x) => x.id === id);
  return r?.name || id;
}

// ============================================================================
// Load Balancers (ALB / NLB) + Target Groups
// ============================================================================
export function LoadBalancersView({ region, goView }) {
  const { byType, state } = useAws();
  const lbs = byType("elb"), tgs = byType("target_group"), instances = byType("instance");
  const [sel, setSel] = useState(null); // {kind:'lb'|'tg', obj}

  const lbDetail = (lb) => {
    const tgNames = lb.target_groups || [];
    return [
      { label: "Details", content: <KV rows={[
        ["Name", lb.name], ["ARN", lb.arn], ["Type", lb.lb_type === "application" ? "Application (Layer 7)" : "Network (Layer 4)"],
        ["Scheme", lb.scheme], ["State", <Status v={lb.state} />], ["DNS name", <span className="font-mono">{lb.dns_name}</span>],
        ["VPC", lb.vpc_id], ["Availability Zones", (lb.subnet_ids || []).map((s) => name(state, s)).join(", ")],
      ]} /> },
      { label: "Listeners", content: (
        <Table cols={["Protocol", "Port", "Default action"]}>
          {(lb.listeners || []).map((l, i) => <tr key={i} className="border-b border-[#2a323d]">
            <Td className="text-slate-300">{l.protocol}</Td><Td className="text-slate-300">{l.port}</Td>
            <Td className="text-slate-300">{l.target_group ? <>Forward to <Link onClick={() => { const t = tgs.find((x) => x.name === l.target_group); if (t) setSel({ kind: "tg", obj: t }); }}>{l.target_group}</Link></> : l.action}</Td></tr>)}
        </Table>) },
      { label: "Networking", content: <>
        <KV rows={[["Scheme", lb.scheme], ["VPC", lb.vpc_id], ["Security groups", (lb.security_group_ids || []).join(", ") || "— (NLB has none)"]]} />
        <Section title="Subnets">
          <Table cols={["Subnet", "Subnet ID"]}>{(lb.subnet_ids || []).map((s) => <tr key={s} className="border-b border-[#2a323d]"><Td className="text-white">{name(state, s)}</Td><Td className="font-mono text-slate-400">{s}</Td></tr>)}</Table>
        </Section></> },
      { label: "Target groups", content: (
        <Table cols={["Name", "Protocol : Port", "Healthy targets"]}>
          {tgNames.map((tn) => { const t = tgs.find((x) => x.name === tn); if (!t) return null;
            const healthy = (t.targets || []).filter((x) => x.health === "healthy").length;
            return <tr key={tn} className="border-b border-[#2a323d]"><Td><Link onClick={() => setSel({ kind: "tg", obj: t })}>{tn}</Link></Td>
              <Td className="text-slate-300">{t.protocol}:{t.port}</Td><Td className="text-emerald-400">{healthy}/{(t.targets || []).length}</Td></tr>; })}
        </Table>) },
    ];
  };

  const tgDetail = (tg) => [
    { label: "Details", content: <KV rows={[
      ["Name", tg.name], ["Target type", tg.target_type], ["Protocol", tg.protocol], ["Port", tg.port],
      ["VPC", tg.vpc_id], ["Health check", tg.health_check],
    ]} /> },
    { label: "Targets", content: (
      <Table cols={["Instance", "Instance ID", "Port", "Health"]}>
        {(tg.targets || []).map((t, i) => { const inst = instances.find((x) => x.id === t.id);
          return <tr key={i} className="border-b border-[#2a323d]">
            <Td>{inst ? <Link onClick={() => goView && goView("ec2")}>{inst.name}</Link> : t.id}</Td>
            <Td className="font-mono text-slate-400">{t.id}</Td><Td className="text-slate-300">{tg.port}</Td>
            <Td><Status v={t.health} /></Td></tr>; })}
      </Table>) },
    { label: "Health check", content: <KV rows={[
      ["Protocol", tg.protocol], ["Path / Port", tg.health_check], ["Healthy threshold", "3"], ["Unhealthy threshold", "2"],
      ["Timeout", "5s"], ["Interval", "30s"], ["Success codes", "200"],
    ]} /> },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="aws-elb-view">
      <Header icon={Scale} title="EC2 · Load Balancers" sub={`${lbs.length} load balancers · ${tgs.length} target groups in ${region}`} />
      <Section title="Load balancers">
        <Table cols={["Name", "DNS name", "Type", "Scheme", "State", "Target groups"]} testid="elb-table">
          {lbs.map((lb) => <tr key={lb.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`elb-row-${lb.name}`}>
            <Td><Link testid={`elb-open-${lb.name}`} onClick={() => setSel({ kind: "lb", obj: lb })}>{lb.name}</Link></Td>
            <Td className="font-mono text-slate-400 text-[11px]">{lb.dns_name}</Td>
            <Td className="text-slate-300">{lb.lb_type === "application" ? "ALB" : "NLB"}</Td>
            <Td className="text-slate-300">{lb.scheme}</Td><Td><Status v={lb.state} /></Td>
            <Td className="text-slate-400">{(lb.target_groups || []).length}</Td></tr>)}
        </Table>
        {lbs.length === 0 && <Empty label="No load balancers in this region." />}
      </Section>
      <Section title="Target groups">
        <Table cols={["Name", "Protocol : Port", "Type", "Healthy / total"]} testid="tg-table">
          {tgs.map((t) => { const healthy = (t.targets || []).filter((x) => x.health === "healthy").length;
            return <tr key={t.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`tg-row-${t.name}`}>
              <Td><Link testid={`tg-open-${t.name}`} onClick={() => setSel({ kind: "tg", obj: t })}>{t.name}</Link></Td>
              <Td className="text-slate-300">{t.protocol}:{t.port}</Td><Td className="text-slate-300">{t.target_type}</Td>
              <Td className="text-emerald-400">{healthy} / {(t.targets || []).length}</Td></tr>; })}
        </Table>
      </Section>
      {sel && <DetailSheet open onClose={() => setSel(null)} testid={sel.kind === "lb" ? "elb-detail-sheet" : "tg-detail-sheet"}
        icon={sel.kind === "lb" ? Scale : HeartPulse}
        title={sel.obj.name} subtitle={sel.obj.arn || sel.obj.id}
        tabs={sel.kind === "lb" ? lbDetail(sel.obj) : tgDetail(sel.obj)} />}
    </div>
  );
}

// ============================================================================
// Auto Scaling (ASGs + Launch templates)
// ============================================================================
export function AutoScalingView({ region, goView }) {
  const { byType } = useAws();
  const asgs = byType("asg"), lts = byType("launch_template"), instances = byType("instance");
  const [sel, setSel] = useState(null); // {kind, obj}

  const asgDetail = (a) => [
    { label: "Details", content: <KV rows={[
      ["Name", a.name], ["Launch template", <Link onClick={() => { const t = lts.find((x) => x.name === a.launch_template); if (t) setSel({ kind: "lt", obj: t }); }}>{a.launch_template}</Link>],
      ["Desired capacity", a.desired], ["Minimum", a.min], ["Maximum", a.max],
      ["Availability Zones", (a.azs || []).join(", ")], ["Health check type", a.health_check],
      ["Attached target group", a.target_group],
    ]} /> },
    { label: "Instances", content: (
      <Table cols={["Instance", "Instance ID", "Lifecycle", "Health"]}>
        {(a.instances || []).map((iid) => { const inst = instances.find((x) => x.id === iid);
          return <tr key={iid} className="border-b border-[#2a323d]">
            <Td>{inst ? <Link onClick={() => goView && goView("ec2")}>{inst.name}</Link> : iid}</Td>
            <Td className="font-mono text-slate-400">{iid}</Td><Td className="text-slate-300">InService</Td>
            <Td><Status v="healthy" /></Td></tr>; })}
      </Table>) },
    { label: "Scaling policies", content: <>
      <div className="text-xs text-slate-400 mb-2">Dynamic scaling policies keep capacity between {a.min} and {a.max} instances.</div>
      <Table cols={["Policy", "Type"]}>{(a.policies || []).map((p, i) => <tr key={i} className="border-b border-[#2a323d]"><Td className="text-white">{p}</Td><Td className="text-slate-300">Target tracking</Td></tr>)}</Table>
    </> },
    { label: "Activity", content: <KV rows={[
      ["Launching a new EC2 instance", "Successful · reached desired capacity"],
      ["Health check", `${a.health_check} · all instances healthy`],
      ["Current / Desired", `${(a.instances || []).length} / ${a.desired}`],
    ]} /> },
  ];

  const ltDetail = (lt) => [
    { label: "Details", content: <KV rows={[
      ["Name", lt.name], ["Default version", lt.default_version], ["Latest version", lt.version],
      ["AMI", lt.config?.ami], ["Instance type", lt.config?.instance_type],
      ["Security group", lt.config?.security_group], ["IAM instance profile", lt.config?.iam_role],
    ]} /> },
    { label: "Versions", content: (
      <Table cols={["Version", "Default", "Instance type"]}>
        {Array.from({ length: lt.version }, (_, i) => i + 1).map((v) => <tr key={v} className="border-b border-[#2a323d]">
          <Td className="text-white">{v}</Td><Td>{v === lt.default_version ? <span className="text-emerald-400">Yes</span> : "—"}</Td>
          <Td className="text-slate-300">{lt.config?.instance_type}</Td></tr>)}
      </Table>) },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="aws-asg-view">
      <Header icon={TrendingUp} title="EC2 · Auto Scaling" sub={`${asgs.length} Auto Scaling groups · ${lts.length} launch templates in ${region}`} />
      <Section title="Auto Scaling groups">
        <Table cols={["Name", "Desired", "Min", "Max", "Instances", "AZs"]} testid="asg-table">
          {asgs.map((a) => <tr key={a.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`asg-row-${a.name}`}>
            <Td><Link testid={`asg-open-${a.name}`} onClick={() => setSel({ kind: "asg", obj: a })}>{a.name}</Link></Td>
            <Td className="text-slate-300">{a.desired}</Td><Td className="text-slate-300">{a.min}</Td><Td className="text-slate-300">{a.max}</Td>
            <Td className="text-emerald-400">{(a.instances || []).length}</Td><Td className="text-slate-400">{(a.azs || []).length}</Td></tr>)}
        </Table>
        {asgs.length === 0 && <Empty label="No Auto Scaling groups in this region." />}
      </Section>
      <Section title="Launch templates">
        <Table cols={["Name", "Default version", "Instance type", "AMI"]} testid="lt-table">
          {lts.map((lt) => <tr key={lt.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`lt-row-${lt.name}`}>
            <Td><Link testid={`lt-open-${lt.name}`} onClick={() => setSel({ kind: "lt", obj: lt })}>{lt.name}</Link></Td>
            <Td className="text-slate-300">v{lt.default_version}</Td><Td className="text-slate-300">{lt.config?.instance_type}</Td>
            <Td className="font-mono text-slate-400">{lt.config?.ami}</Td></tr>)}
        </Table>
      </Section>
      {sel && <DetailSheet open onClose={() => setSel(null)} testid={sel.kind === "asg" ? "asg-detail-sheet" : "lt-detail-sheet"}
        icon={sel.kind === "asg" ? TrendingUp : Layers} title={sel.obj.name} subtitle={sel.obj.id}
        tabs={sel.kind === "asg" ? asgDetail(sel.obj) : ltDetail(sel.obj)} />}
    </div>
  );
}

// ============================================================================
// RDS (hands-on) — create instances, lifecycle, databases + real SQL query
// ============================================================================
export function RdsView({ region }) {
  const { byType, state, run, action, refresh } = useAws();
  const dbs = byType("db_instance"), groups = byType("db_subnet_group"), snaps = byType("db_snapshot");
  const subnetGroups = groups, sgs = byType("security_group");
  const [sel, setSel] = useState(null); // {kind:'db', id} | {kind:'group', obj}
  const [wizard, setWizard] = useState(false);
  const selDb = sel?.kind === "db" ? dbs.find((d) => d.id === sel.id) : null;

  const doAction = async (name, body, okMsg) => {
    const r = await run(() => action(name, body));
    if (r.ok) { toast.success(okMsg); } else { toast.error(r.error); }
    return r.ok;
  };

  const lifecycleBar = (d) => (
    <div className="flex flex-wrap gap-2" data-testid="rds-lifecycle-bar">
      <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5 gap-1.5"
        disabled={d.status !== "stopped"} data-testid="rds-start"
        onClick={() => doAction("rds-instance-action", { db_id: d.id, action: "start" }, "Starting DB instance")}><Play className="h-3.5 w-3.5" /> Start</Button>
      <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5 gap-1.5"
        disabled={d.status !== "available"} data-testid="rds-stop"
        onClick={() => doAction("rds-instance-action", { db_id: d.id, action: "stop" }, "Stopping DB instance")}><SquareIcon className="h-3.5 w-3.5" /> Stop</Button>
      <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5 gap-1.5"
        disabled={d.status !== "available"} data-testid="rds-reboot"
        onClick={() => doAction("rds-instance-action", { db_id: d.id, action: "reboot" }, "Rebooting DB instance")}><RotateCw className="h-3.5 w-3.5" /> Reboot</Button>
      <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-1.5"
        data-testid="rds-delete"
        onClick={async () => { if (await doAction("rds-instance-action", { db_id: d.id, action: "delete" }, "Deleting DB instance")) setSel(null); }}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
    </div>
  );

  const dbDetail = (d) => {
    const tabs = [
      { label: "Details", content: <>
        {lifecycleBar(d)}
        <KV rows={[
          ["DB identifier", d.id], ["ARN", d.arn], ["Engine", `${d.engine} ${d.engine_version}`], ["Role", d.role],
          ["Class", d.db_class], ["Status", <Status v={d.status} />], ["Multi-AZ", d.multi_az ? "Yes" : "No"],
          ["AZ", d.az], ["Est. cost", d.cost_hr ? `$${d.cost_hr}/hr` : "—"],
          d.source_db && ["Replication source", d.source_db], d.replication_lag_ms != null && ["Replica lag", `${d.replication_lag_ms} ms`],
        ]} />
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-400">Deletion protection</span>
          <Switch checked={!!d.deletion_protection} data-testid="rds-deletion-protection"
            onCheckedChange={(v) => doAction("rds-set-deletion-protection", { db_id: d.id, enabled: v }, v ? "Deletion protection enabled" : "Deletion protection disabled")} />
          <span className={d.deletion_protection ? "text-emerald-400" : "text-slate-500"}>{d.deletion_protection ? "Enabled" : "Disabled"}</span>
        </div>
      </> },
      { label: "Connectivity & security", content: <>
        <KV rows={[
          ["Endpoint", <span className="font-mono">{d.endpoint}</span>], ["Port", d.port], ["Master username", d.master_username || "admin"],
          ["VPC", d.vpc_id || "—"], ["Subnet group", d.subnet_group || "—"],
          ["Security groups", (d.security_group_ids || []).join(", ") || "—"], ["Publicly accessible", d.publicly_accessible ? "Yes" : "No"],
        ]} />
        <Section title="Encryption"><KV rows={[["Encryption at rest", d.encrypted ? "Enabled" : "Disabled"], ["KMS key", d.kms || "—"]]} /></Section>
      </> },
      { label: "Maintenance & backups", content: <KV rows={[
        ["Automated backups", "Enabled"], ["Backup retention", `${d.backup_retention} days`], ["Storage", `${d.storage_gb} GiB ${d.storage_type}`],
        ["Latest snapshot", (snaps.find((s) => s.db === d.id) || {}).name || "—"],
      ]} /> },
      { label: "Databases & query", content: <DbQueryPanel db={d} refresh={refresh} run={run} action={action} /> },
    ];
    return tabs;
  };
  const groupDetail = (g) => [
    { label: "Details", content: <KV rows={[["Name", g.name], ["VPC", g.vpc_id], ["AZs", (g.azs || []).join(", ")]]} /> },
    { label: "Subnets", content: <Table cols={["Subnet", "ID"]}>{(g.subnet_ids || []).map((s) => <tr key={s} className="border-b border-[#2a323d]"><Td className="text-white">{name(state, s)}</Td><Td className="font-mono text-slate-400">{s}</Td></tr>)}</Table> },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="aws-rds-view">
      <Header icon={Database} title="RDS · Databases" sub={`${dbs.length} DB instances in ${region}`}
        right={<Button size="sm" className="gap-1.5 bg-[#ff9900] hover:bg-[#e88b00] text-black font-medium" data-testid="rds-create-open" onClick={() => setWizard(true)}><Plus className="h-4 w-4" /> Create database</Button>} />
      <Section title="Databases">
        <Table cols={["DB identifier", "Role", "Engine", "Class", "Multi-AZ", "Status"]} testid="rds-table">
          {dbs.map((d) => <tr key={d.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`rds-row-${d.id}`}>
            <Td><Link testid={`rds-open-${d.id}`} onClick={() => setSel({ kind: "db", id: d.id })}>{d.id}</Link></Td>
            <Td className="text-slate-300">{d.role}</Td><Td className="text-slate-300">{d.engine} {d.engine_version}</Td>
            <Td className="text-slate-300">{d.db_class}</Td><Td className="text-slate-400">{d.multi_az ? "Yes" : "No"}</Td>
            <Td><Status v={d.status} /></Td></tr>)}
        </Table>
        {dbs.length === 0 && <Empty label="No databases in this region. Click “Create database” to launch one." />}
      </Section>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Subnet groups">
          <Table cols={["Name", "Subnets", "VPC"]}>{groups.map((g) => <tr key={g.id} className="border-b border-[#2a323d]">
            <Td><Link onClick={() => setSel({ kind: "group", obj: g })}>{g.name}</Link></Td><Td className="text-slate-300">{(g.subnet_ids || []).length}</Td><Td className="font-mono text-slate-400">{g.vpc_id}</Td></tr>)}</Table>
        </Section>
        <Section title="Snapshots">
          <Table cols={["Snapshot", "Source DB", "Size", "Status"]}>{snaps.map((s) => <tr key={s.id} className="border-b border-[#2a323d]">
            <Td className="text-white">{s.name}</Td><Td className="text-slate-400">{s.db}</Td><Td className="text-slate-300">{s.size_gb} GiB</Td><Td><Status v={s.status} /></Td></tr>)}</Table>
        </Section>
      </div>
      {sel?.kind === "db" && selDb && <DetailSheet open onClose={() => setSel(null)} testid="rds-detail-sheet" icon={Database}
        title={selDb.id} subtitle={selDb.arn || selDb.id} tabs={dbDetail(selDb)} />}
      {sel?.kind === "group" && <DetailSheet open onClose={() => setSel(null)} testid="rds-detail-sheet" icon={Boxes}
        title={sel.obj.name} subtitle={sel.obj.id} tabs={groupDetail(sel.obj)} />}
      {wizard && <CreateDbWizard onClose={() => setWizard(false)} run={run} action={action}
        subnetGroups={subnetGroups} sgs={sgs} region={region}
        onCreated={(id) => { setWizard(false); setSel({ kind: "db", id }); }} />}
    </div>
  );
}

// ---- Create DB instance wizard ----
function CreateDbWizard({ onClose, run, action, subnetGroups, sgs, region, onCreated }) {
  const [catalog, setCatalog] = useState({ engines: [], classes: [] });
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    db_identifier: "", engine: "mysql", engine_version: "", db_class: "db.t3.micro",
    storage_gb: 20, multi_az: false, encrypted: true, master_username: "admin",
    initial_db: "appdb", subnet_group: "", security_group_ids: [], publicly_accessible: false,
  });
  useEffect(() => { api.get("/aws/rds/catalog").then(({ data }) => setCatalog(data)); }, []);
  const eng = (catalog.engines || []).find((e) => e.engine === f.engine);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    const body = { ...f, engine_version: f.engine_version || (eng?.versions?.[0]) };
    const r = await run(() => action("rds-create-db-instance", body));
    setBusy(false);
    if (r.ok) { toast.success(`DB instance “${f.db_identifier}” is available`); onCreated(f.db_identifier); }
    else toast.error(r.error);
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-[#12203a] border-l border-[#414750] text-slate-100 w-full sm:max-w-xl overflow-y-auto" data-testid="rds-create-wizard">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-white flex items-center gap-2"><Database className="h-5 w-5 text-[#ff9900]" /> Create database</SheetTitle>
          <SheetDescription className="text-slate-400 text-xs">Launch a new Amazon RDS DB instance in {region}.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4">
          <Field label="DB instance identifier">
            <Input value={f.db_identifier} onChange={(e) => set("db_identifier", e.target.value)} placeholder="my-database-1"
              className="bg-[#0f1b2d] border-[#414750]" data-testid="rds-field-identifier" />
            <p className="text-[11px] text-slate-500 mt-1">Lowercase letters, numbers and hyphens; must start with a letter.</p>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Engine">
              <SelWrap value={f.engine} onChange={(v) => { set("engine", v); set("engine_version", ""); }} testid="rds-field-engine"
                options={(catalog.engines || []).map((e) => [e.engine, e.label])} />
            </Field>
            <Field label="Version">
              <SelWrap value={f.engine_version || (eng?.versions?.[0] || "")} onChange={(v) => set("engine_version", v)} testid="rds-field-version"
                options={(eng?.versions || []).map((v) => [v, v])} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="DB instance class">
              <SelWrap value={f.db_class} onChange={(v) => set("db_class", v)} testid="rds-field-class"
                options={(catalog.classes || []).map((c) => [c.name, `${c.name} · ${c.vcpu} vCPU / ${c.mem_gib} GiB · $${c.cost_hr}/hr`])} />
            </Field>
            <Field label="Allocated storage (GiB)">
              <Input type="number" value={f.storage_gb} onChange={(e) => set("storage_gb", Number(e.target.value))}
                className="bg-[#0f1b2d] border-[#414750]" data-testid="rds-field-storage" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Master username">
              <Input value={f.master_username} onChange={(e) => set("master_username", e.target.value)}
                className="bg-[#0f1b2d] border-[#414750]" data-testid="rds-field-master" />
            </Field>
            <Field label="Initial database name (optional)">
              <Input value={f.initial_db} onChange={(e) => set("initial_db", e.target.value)} placeholder="appdb"
                className="bg-[#0f1b2d] border-[#414750]" data-testid="rds-field-initialdb" />
            </Field>
          </div>
          <Field label="DB subnet group (optional)">
            <SelWrap value={f.subnet_group || "__none__"} onChange={(v) => set("subnet_group", v === "__none__" ? "" : v)} testid="rds-field-subnetgroup"
              options={[["__none__", "— none —"], ...subnetGroups.map((g) => [g.name, g.name])]} />
          </Field>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={f.multi_az} onCheckedChange={(v) => set("multi_az", v)} data-testid="rds-field-multiaz" /> Multi-AZ deployment</label>
            <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={f.encrypted} onCheckedChange={(v) => set("encrypted", v)} data-testid="rds-field-encrypted" /> Encryption</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={onClose}>Cancel</Button>
            <Button className="bg-[#ff9900] hover:bg-[#e88b00] text-black font-medium gap-1.5" disabled={busy || !f.db_identifier} onClick={submit} data-testid="rds-create-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Create database
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-medium text-slate-300 block mb-1.5">{label}</label>{children}</div>;
}
function SelWrap({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-[#0f1b2d] border-[#414750] text-slate-100" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#12203a] border-[#414750] text-slate-100">
        {options.map(([v, l]) => <SelectItem key={v} value={v} className="text-slate-100 focus:bg-white/10">{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// ---- Databases + real SQL query panel (inside a DB instance) ----
function DbQueryPanel({ db, refresh, run, action }) {
  const databases = db.databases || [];
  const [activeDb, setActiveDb] = useState(databases[0] || "");
  const [newDb, setNewDb] = useState("");
  const [sql, setSql] = useState("SELECT name FROM sqlite_master WHERE type='table';");
  const [readonly, setReadonly] = useState(false);
  const [result, setResult] = useState(null);
  const [tables, setTables] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!activeDb && databases[0]) setActiveDb(databases[0]); }, [databases]); // eslint-disable-line
  const loadTables = async (dbName) => {
    if (!dbName) return setTables([]);
    try { const { data } = await api.get(`/aws/rds/${db.id}/databases/${dbName}/tables`); setTables(data.tables || []); }
    catch { setTables([]); }
  };
  useEffect(() => { loadTables(activeDb); }, [activeDb, db.status]); // eslint-disable-line

  if (db.status !== "available") {
    return <div className="text-sm text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-lg p-4">The DB instance is <strong>{db.status}</strong>. Start it to manage databases and run queries.</div>;
  }

  const createDatabase = async () => {
    const r = await run(() => action("rds-create-database", { db_id: db.id, name: newDb.trim() }));
    if (r.ok) { toast.success(`Database “${newDb.trim()}” created`); setActiveDb(newDb.trim()); setNewDb(""); }
    else toast.error(r.error);
  };
  const dropDatabase = async (n) => {
    const r = await run(() => action("rds-drop-database", { db_id: db.id, name: n }));
    if (r.ok) { toast.success(`Database “${n}” dropped`); if (activeDb === n) setActiveDb((db.databases || []).filter((x) => x !== n)[0] || ""); }
    else toast.error(r.error);
  };
  const runQuery = async () => {
    if (!activeDb) { toast.error("Select or create a database first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/aws/rds/${db.id}/query`, { database: activeDb, sql, readonly });
      setResult(data);
      if (data.error) toast.error(data.message || "Query error"); else loadTables(activeDb);
    } catch (e) { toast.error(e?.response?.data?.detail?.error?.message || "Query failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="rds-query-panel">
      {/* databases on this instance */}
      <Section title="Databases on this instance">
        <div className="flex flex-wrap gap-2 mb-2">
          {databases.map((n) => (
            <span key={n} className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border text-xs cursor-pointer ${activeDb === n ? "border-[#ff9900] text-[#ff9900] bg-[#ff9900]/10" : "border-[#414750] text-slate-300"}`}
              onClick={() => setActiveDb(n)} data-testid={`rds-db-${n}`}>
              <Database className="h-3 w-3" /> {n}
              <button className="hover:text-rose-400" data-testid={`rds-dropdb-${n}`} onClick={(e) => { e.stopPropagation(); dropDatabase(n); }}><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
          {databases.length === 0 && <span className="text-xs text-slate-500">No databases yet — create one below.</span>}
        </div>
        <div className="flex gap-2">
          <Input value={newDb} onChange={(e) => setNewDb(e.target.value)} placeholder="new_database_name"
            className="bg-[#0f1b2d] border-[#414750] h-8 text-sm max-w-xs" data-testid="rds-newdb-input" />
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5 gap-1.5" disabled={!newDb.trim()} onClick={createDatabase} data-testid="rds-newdb-create"><Plus className="h-3.5 w-3.5" /> Create database</Button>
        </div>
      </Section>

      {/* query editor */}
      <Section title={<span className="flex items-center gap-1.5"><Terminal className="h-4 w-4 text-[#ff9900]" /> Query editor {activeDb && <span className="text-slate-400 font-normal">· {activeDb}</span>}</span>}>
        <textarea value={sql} onChange={(e) => setSql(e.target.value)} spellCheck={false} data-testid="rds-sql-editor"
          className="w-full h-28 bg-[#0b1020] text-emerald-200 font-mono text-xs p-3 rounded-lg border border-[#414750] outline-none resize-none" />
        <div className="flex items-center gap-3 mt-2">
          <Button size="sm" className="bg-[#ff9900] hover:bg-[#e88b00] text-black font-medium gap-1.5" disabled={busy} onClick={runQuery} data-testid="rds-run-query">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run
          </Button>
          <label className="flex items-center gap-2 text-xs text-slate-400"><Switch checked={readonly} onCheckedChange={setReadonly} data-testid="rds-readonly-toggle" /> read-only role</label>
          {tables.length > 0 && <span className="text-xs text-slate-500 ml-auto">{tables.length} table(s): {tables.map((t) => t.table).join(", ")}</span>}
        </div>
      </Section>

      {/* results */}
      {result && (result.error
        ? <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs font-mono p-3" data-testid="rds-query-error">{result.message}</div>
        : <div data-testid="rds-query-results">
            {(result.columns || []).length > 0 && (
              <div className="rounded-lg border border-[#414750] overflow-auto max-h-64">
                <table className="w-full text-xs">
                  <thead className="bg-[#1f2937] sticky top-0"><tr>{result.columns.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
                  <tbody>{(result.rows || []).map((r, i) => <tr key={i} className="border-b border-[#2a323d]">{r.map((v, j) => <Td key={j} className="font-mono">{v === null ? <span className="text-slate-500 italic">NULL</span> : String(v)}</Td>)}</tr>)}</tbody>
                </table>
              </div>
            )}
            <div className="text-[11px] text-slate-400 mt-1.5">{result.message}</div>
          </div>)}
    </div>
  );
}


// ============================================================================
// ElastiCache (Redis)
// ============================================================================
export function ElastiCacheView({ region }) {
  const { byType } = useAws();
  const clusters = byType("cache_cluster");
  const [sel, setSel] = useState(null);
  const detail = (c) => [
    { label: "Details", content: <KV rows={[
      ["Cluster", c.id], ["Engine", `${c.engine} ${c.engine_version}`], ["Node type", c.node_type],
      ["Status", <Status v={c.status} />], ["Nodes", c.node_count], ["Replication", c.replication],
      ["Est. cost", c.cost_hr ? `$${c.cost_hr}/hr` : "—"],
    ]} /> },
    { label: "Connectivity & security", content: <KV rows={[
      ["Primary endpoint", <span className="font-mono">{c.endpoint}</span>], ["Port", c.port], ["VPC", c.vpc_id],
      ["Subnet group", c.subnet_group], ["Security groups", (c.security_group_ids || []).join(", ")],
      ["Encryption at rest", c.encrypted ? "Enabled" : "Disabled"], ["AUTH token", c.auth],
      ["Availability Zones", (c.azs || []).join(", ")],
    ]} /> },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="aws-cache-view">
      <Header icon={MemoryStick} title="ElastiCache · Redis" sub={`${clusters.length} clusters in ${region}`} />
      <Table cols={["Cluster", "Engine", "Node type", "Nodes", "Status", "Endpoint"]} testid="cache-table">
        {clusters.map((c) => <tr key={c.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`cache-row-${c.id}`}>
          <Td><Link testid={`cache-open-${c.id}`} onClick={() => setSel(c)}>{c.id}</Link></Td>
          <Td className="text-slate-300">{c.engine} {c.engine_version}</Td><Td className="text-slate-300">{c.node_type}</Td>
          <Td className="text-slate-400">{c.node_count}</Td><Td><Status v={c.status} /></Td>
          <Td className="font-mono text-slate-400 text-[11px]">{c.endpoint}</Td></tr>)}
      </Table>
      {clusters.length === 0 && <Empty label="No cache clusters in this region." />}
      {sel && <DetailSheet open onClose={() => setSel(null)} testid="cache-detail-sheet" icon={MemoryStick} title={sel.id} subtitle={sel.engine} tabs={detail(sel)} />}
    </div>
  );
}

// ============================================================================
// Route 53 (Hosted zones)
// ============================================================================
export function Route53View() {
  const { byType } = useAws();
  const zones = byType("hosted_zone");
  const [sel, setSel] = useState(null);
  const detail = (z) => [
    { label: "Records", content: (
      <Table cols={["Record name", "Type", "Value / route", "TTL", "Routing"]}>
        {(z.records || []).map((r, i) => <tr key={i} className="border-b border-[#2a323d]">
          <Td className="text-white">{r.name}</Td><Td className="text-slate-300">{r.type}</Td>
          <Td className="font-mono text-slate-400 text-[11px]">{r.value}</Td><Td className="text-slate-400">{r.ttl}</Td>
          <Td className="text-slate-400">{r.routing}</Td></tr>)}
      </Table>) },
    { label: "Details", content: <KV rows={[["Domain", z.domain], ["Type", z.zone_type], ["Records", z.record_count], ["Hosted zone ID", z.id]]} /> },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="aws-route53-view">
      <Header icon={Globe2} title="Route 53 · Hosted zones" sub={`${zones.length} hosted zones`} />
      <Table cols={["Domain", "Type", "Records", "Zone ID"]} testid="route53-table">
        {zones.map((z) => <tr key={z.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`route53-row-${z.domain}`}>
          <Td><Link testid={`route53-open-${z.domain}`} onClick={() => setSel(z)}>{z.domain}</Link></Td>
          <Td className="text-slate-300">{z.zone_type}</Td><Td className="text-slate-400">{z.record_count}</Td>
          <Td className="font-mono text-slate-400">{z.id}</Td></tr>)}
      </Table>
      {zones.length === 0 && <Empty label="No hosted zones." />}
      {sel && <DetailSheet open onClose={() => setSel(null)} testid="route53-detail-sheet" icon={Globe2} title={sel.domain} subtitle={sel.id} tabs={detail(sel)} />}
    </div>
  );
}

// ============================================================================
// CloudFront (distributions)
// ============================================================================
export function CloudFrontView() {
  const { byType } = useAws();
  const dists = byType("cloudfront");
  const [sel, setSel] = useState(null);
  const detail = (c) => [
    { label: "Details", content: <KV rows={[
      ["Distribution", c.name], ["ID", c.id], ["Domain name", <span className="font-mono">{c.domain_name}</span>],
      ["Status", <Status v={c.status} />], ["Price class", c.price_class], ["SSL certificate", c.certificate],
      ["Attached WAF", c.waf],
    ]} /> },
    { label: "Origins", content: <Table cols={["Origin"]}>{(c.origins || []).map((o, i) => <tr key={i} className="border-b border-[#2a323d]"><Td className="text-slate-200 font-mono text-[11px]">{o}</Td></tr>)}</Table> },
    { label: "Behaviors", content: <Table cols={["Path pattern → origin"]}>{(c.behaviors || []).map((b, i) => <tr key={i} className="border-b border-[#2a323d]"><Td className="text-slate-200">{b}</Td></tr>)}</Table> },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="aws-cloudfront-view">
      <Header icon={Network} title="CloudFront · Distributions" sub={`${dists.length} distributions`} />
      <Table cols={["Distribution", "Domain name", "Status", "WAF", "Price class"]} testid="cloudfront-table">
        {dists.map((c) => <tr key={c.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`cloudfront-row-${c.name}`}>
          <Td><Link testid={`cloudfront-open-${c.name}`} onClick={() => setSel(c)}>{c.name}</Link></Td>
          <Td className="font-mono text-slate-400 text-[11px]">{c.domain_name}</Td><Td><Status v={c.status} /></Td>
          <Td className="text-slate-400">{c.waf}</Td><Td className="text-slate-400">{c.price_class}</Td></tr>)}
      </Table>
      {dists.length === 0 && <Empty label="No distributions." />}
      {sel && <DetailSheet open onClose={() => setSel(null)} testid="cloudfront-detail-sheet" icon={Network} title={sel.name} subtitle={sel.id} tabs={detail(sel)} />}
    </div>
  );
}

// ============================================================================
// WAF (web ACLs)
// ============================================================================
export function WafView() {
  const { byType } = useAws();
  const acls = byType("waf_acl");
  const [sel, setSel] = useState(null);
  const detail = (w) => [
    { label: "Rules", content: (
      <Table cols={["Priority", "Rule", "Action"]}>
        {(w.rules || []).slice().sort((a, b) => a.priority - b.priority).map((r, i) => <tr key={i} className="border-b border-[#2a323d]">
          <Td className="text-slate-400">{r.priority}</Td><Td className="text-white">{r.name}</Td>
          <Td className={r.action.startsWith("Block") ? "text-rose-400" : "text-slate-300"}>{r.action}</Td></tr>)}
      </Table>) },
    { label: "Details", content: <KV rows={[
      ["Name", w.name], ["Scope", w.scope], ["Default action", w.default_action], ["Associated with", (w.associated || []).join(", ")],
    ]} /> },
    { label: "Traffic (24h)", content: <div className="grid grid-cols-2 gap-4">
      <div className="rounded-lg border border-[#414750] bg-[#0f1b2d] p-4"><div className="text-2xl font-semibold text-emerald-400">{(w.allowed_requests_24h || 0).toLocaleString()}</div><div className="text-xs text-slate-400 mt-1">Allowed requests</div></div>
      <div className="rounded-lg border border-[#414750] bg-[#0f1b2d] p-4"><div className="text-2xl font-semibold text-rose-400">{(w.blocked_requests_24h || 0).toLocaleString()}</div><div className="text-xs text-slate-400 mt-1">Blocked requests</div></div>
    </div> },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="aws-waf-view">
      <Header icon={ShieldCheck} title="WAF & Shield · Web ACLs" sub={`${acls.length} web ACLs`} />
      <Table cols={["Name", "Scope", "Default action", "Rules", "Blocked (24h)"]} testid="waf-table">
        {acls.map((w) => <tr key={w.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`waf-row-${w.name}`}>
          <Td><Link testid={`waf-open-${w.name}`} onClick={() => setSel(w)}>{w.name}</Link></Td>
          <Td className="text-slate-300">{w.scope}</Td><Td className="text-slate-300">{w.default_action}</Td>
          <Td className="text-slate-400">{(w.rules || []).length}</Td><Td className="text-rose-400">{(w.blocked_requests_24h || 0).toLocaleString()}</Td></tr>)}
      </Table>
      {acls.length === 0 && <Empty label="No web ACLs." />}
      {sel && <DetailSheet open onClose={() => setSel(null)} testid="waf-detail-sheet" icon={ShieldCheck} title={sel.name} subtitle={sel.scope} tabs={detail(sel)} />}
    </div>
  );
}

// ============================================================================
// KMS (customer managed keys)
// ============================================================================
export function KmsView({ region }) {
  const { byType } = useAws();
  const keys = byType("kms_key");
  const [sel, setSel] = useState(null);
  const detail = (k) => [
    { label: "Details", content: <KV rows={[
      ["Alias", k.alias], ["Key ID", k.id], ["ARN", k.arn], ["State", <Status v={k.state} />],
      ["Automatic rotation", k.rotation ? "Enabled (365 days)" : "Disabled"], ["Description", k.description],
    ]} /> },
    { label: "Key users", content: <Table cols={["Principal"]}>{(k.key_users || []).map((u, i) => <tr key={i} className="border-b border-[#2a323d]"><Td className="text-slate-200">{u}</Td></tr>)}</Table> },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="aws-kms-view">
      <Header icon={KeyRound} title="KMS · Customer managed keys" sub={`${keys.length} keys in ${region}`} />
      <Table cols={["Alias", "Key ID", "State", "Rotation"]} testid="kms-table">
        {keys.map((k) => <tr key={k.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`kms-row-${k.name}`}>
          <Td><Link testid={`kms-open-${k.name}`} onClick={() => setSel(k)}>{k.alias}</Link></Td>
          <Td className="font-mono text-slate-400">{k.id}</Td><Td><Status v={k.state} /></Td>
          <Td className="text-emerald-400">{k.rotation ? "Enabled" : "Disabled"}</Td></tr>)}
      </Table>
      {keys.length === 0 && <Empty label="No customer managed keys." />}
      {sel && <DetailSheet open onClose={() => setSel(null)} testid="kms-detail-sheet" icon={KeyRound} title={sel.alias} subtitle={sel.id} tabs={detail(sel)} />}
    </div>
  );
}

// ============================================================================
// Secrets Manager
// ============================================================================
export function SecretsView({ region }) {
  const { byType } = useAws();
  const secrets = byType("secret");
  const [sel, setSel] = useState(null);
  const [reveal, setReveal] = useState(false);
  const open = (s) => { setReveal(false); setSel(s); };
  const detail = (s) => [
    { label: "Details", content: <KV rows={[
      ["Name", s.name], ["ARN", s.arn], ["Description", s.description], ["Encryption key", s.kms],
      ["Rotation", s.rotation], ["Last rotated", s.last_rotated ? new Date(s.last_rotated).toLocaleString() : "—"],
    ]} /> },
    { label: "Secret value", content: <>
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5 gap-1.5" onClick={() => setReveal((r) => !r)} data-testid="secret-reveal-btn">
          {reveal ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Retrieve secret value</>}</Button>
      </div>
      <Table cols={["Key", "Value"]}>
        {(s.secret_keys || []).map((k) => <tr key={k} className="border-b border-[#2a323d]">
          <Td className="text-slate-300 font-mono">{k}</Td>
          <Td className="font-mono text-slate-100">{reveal ? mockSecretValue(k) : "••••••••••••"}</Td></tr>)}
      </Table></> },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="aws-secrets-view">
      <Header icon={Lock} title="Secrets Manager" sub={`${secrets.length} secrets in ${region}`} />
      <Table cols={["Name", "Description", "Rotation", "Last rotated"]} testid="secrets-table">
        {secrets.map((s) => <tr key={s.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`secret-row-${s.name}`}>
          <Td><Link testid={`secret-open-${s.name}`} onClick={() => open(s)}>{s.name}</Link></Td>
          <Td className="text-slate-400">{s.description}</Td><Td className="text-emerald-400">{s.rotation}</Td>
          <Td className="text-slate-400">{s.last_rotated ? new Date(s.last_rotated).toLocaleDateString() : "—"}</Td></tr>)}
      </Table>
      {secrets.length === 0 && <Empty label="No secrets." />}
      {sel && <DetailSheet open onClose={() => setSel(null)} testid="secret-detail-sheet" icon={Lock} title={sel.name} subtitle={sel.arn} tabs={detail(sel)} />}
    </div>
  );
}
function mockSecretValue(k) {
  const m = { username: "hmg_app_admin", password: "R3d@cted-Tr@ining-9F2c!", host: "hmg-prod-mysql-primary.ap-northeast-1.rds.amazonaws.com", port: "3306", dbname: "hmgportal" };
  return m[k] || "training-value";
}

// ============================================================================
// AWS Config (compliance)
// ============================================================================
export function ConfigView() {
  const { byType } = useAws();
  const rules = byType("config_rule");
  const compliant = rules.filter((r) => r.compliance === "COMPLIANT").length;
  const non = rules.length - compliant;
  return (
    <div className="p-6 space-y-6" data-testid="aws-config-view">
      <Header icon={ClipboardCheck} title="AWS Config · Compliance" sub={`${rules.length} rules evaluated`} />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-[#414750] bg-[#161e2d] p-4"><div className="text-3xl font-semibold text-emerald-400">{compliant}</div><div className="text-xs text-slate-400 mt-1">Compliant rules</div></div>
        <div className="rounded-lg border border-[#414750] bg-[#161e2d] p-4"><div className="text-3xl font-semibold text-rose-400">{non}</div><div className="text-xs text-slate-400 mt-1">Non-compliant rules</div></div>
        <div className="rounded-lg border border-[#414750] bg-[#161e2d] p-4"><div className="text-3xl font-semibold text-white">{rules.length ? Math.round((compliant / rules.length) * 100) : 0}%</div><div className="text-xs text-slate-400 mt-1">Overall compliance</div></div>
      </div>
      <Table cols={["Rule name", "Compliance", "Result"]} testid="config-table">
        {rules.map((r) => <tr key={r.id} className="border-b border-[#2a323d]" data-testid={`config-row-${r.name}`}>
          <Td className="text-white">{r.name}</Td><Td><Status v={r.compliance} /></Td><Td className="text-slate-400">{r.result}</Td></tr>)}
      </Table>
      {rules.length === 0 && <Empty label="No Config rules." />}
    </div>
  );
}
